import { useCallback, useState } from 'react';
import { useToast } from '../../../../hooks/useToast';
import { fetchMaterialContentBlob } from '../../../../features/materials/api';
import { listStoryboards } from '../../../../features/scenarios/api';
import { useQueryClient } from '@tanstack/react-query';
import {
  downloadScenarioAsZip,
  buildScenarioExportJson,
  type ScenarioDownloadData,
} from '../../../../features/scenarios/downloadUtils';
import type { Scenario } from '../types';
import type { Storyboard } from '../../../../features/scenarios/types';
import { parseSceneRange } from '../../../../features/scenarios/download/storyboardSegments';
import { useSceneStateContext } from './SceneStateContext';

/**
 * シナリオ末尾の「ファイルDL」(ZIP 一括) と、シーン単位の素材 DL・シナリオ JSON コピー。
 * プレビュー動画 / 連結 / YouTube / XML / FCPXML / Canva の各出力は動画エディタへの一本化に伴い撤去した。
 */
export function useScenarioExport() {
  const {
    scenePreviewMaterials, sceneLastFrames,
    confirmedFirstFrames, sceneVideoMaterialId,
  } = useSceneStateContext();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [downloadingMaterials, setDownloadingMaterials] = useState<Set<string>>(new Set());
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadMaterial = useCallback(async (materialId: string, filenamePrefix: string) => {
    setDownloadingMaterials(prev => new Set(prev).add(materialId));
    try {
      const blob = await fetchMaterialContentBlob(materialId);
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const ext = blob.type.includes('video') ? 'mp4' : blob.type.includes('audio') ? 'mp3' : 'bin';
      link.download = `${filenamePrefix}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('ダウンロードに失敗しました');
    } finally {
      setDownloadingMaterials(prev => {
        const next = new Set(prev);
        next.delete(materialId);
        return next;
      });
    }
  }, [toast]);

  const collectScenarioDownloadData = useCallback(
    async (scenario: Scenario): Promise<ScenarioDownloadData> => {
      const scenesData = scenario.scenes.map((scene, idx) => {
        const key = `${scenario.id}-scene-${scene.order}`;
        const materials = scenePreviewMaterials[key] || [];

        // 確定済みの最初のフレームを取得
        let firstFrameMaterialId: string | undefined;
        const confirmedId = confirmedFirstFrames[key];
        if (confirmedId) {
          firstFrameMaterialId = confirmedId;
        } else if (scene.image_material_id) {
          firstFrameMaterialId = scene.image_material_id;
        } else if (materials.length === 1 && materials[0]?.id) {
          firstFrameMaterialId = materials[0].id;
        }

        // 最後のフレーム
        const lastFrameData = sceneLastFrames[key];
        let lastFrameMaterialId = lastFrameData?.materialId || scene.last_frame_material_id;

        // 次のシーンの最初のフレームと同一素材なら最後のフレームをスキップ
        if (lastFrameMaterialId && idx < scenario.scenes.length - 1) {
          const nextScene = scenario.scenes[idx + 1];
          const nextKey = `${scenario.id}-scene-${nextScene.order}`;
          const nextConfirmedId = confirmedFirstFrames[nextKey];
          const nextFirstFrameId = nextConfirmedId || nextScene.image_material_id;
          if (nextFirstFrameId && lastFrameMaterialId === nextFirstFrameId) {
            lastFrameMaterialId = undefined;
          }
        }

        // 動画（ローカルstateまたはDBの値を使用）。「採用しない (video_excluded)」の
        // シーンは未生成扱いにして ZIP/XML/JSON に含めない。
        const videoMaterialId = scene.video_excluded
          ? undefined
          : (sceneVideoMaterialId[key] || scene.video_material_id);

        // 動画カードの担当範囲 (保存済み scene_range)。XML エクスポートのセグメント分割で
        // 使う (カードのアンカー位置と担当範囲はズレ得るため、アンカー位置では区切れない)
        const sceneRange = parseSceneRange(scene.generation_metadata);

        return {
          sceneId: scene.id,
          sceneOrder: scene.order,
          setting: scene.setting,
          visual: scene.visual,
          script: scene.script,
          narration: scene.narration || '',
          dialogue: scene.dialogue || '',
          telop: scene.telop || '',
          firstFrameMaterialId,
          lastFrameMaterialId,
          videoMaterialId,
          // 前面音声(ナレーション枠)はサウンドロゴで置換され得る。エクスポートでは
          // 種別に応じた素材/尺を narration スロットに流し込み、既存の音声配置ロジックで扱う。
          // 「採用しない (narration_excluded)」のシーンは音声・尺とも含めない。
          narrationMaterialId: scene.narration_excluded
            ? undefined
            : (scene.audio_track_type === 'sound_logo'
              ? scene.sound_logo_material_id
              : scene.narration_material_id),
          videoDurationSeconds: scene.video_excluded
            ? undefined
            : (scene.video_duration_seconds
              || (scene.generation_metadata?.duration_seconds as number | undefined)),
          narrationDurationSeconds: scene.narration_excluded
            ? undefined
            : (scene.audio_track_type === 'sound_logo'
              ? scene.sound_logo_duration_seconds
              : scene.narration_duration_seconds),
          storyboardDurationSeconds: scene.storyboard_duration_seconds,
          sceneRange,
          brandCutPosition: scene.brand_cut_position,
          brandCutDurationSeconds: scene.brand_cut_duration_seconds,
        };
      });

      // ストーリーボードモードでは選択中の絵コンテ画像 (シナリオ全体で 1 枚) を同梱する。
      // storyboards は Scenario に載っていないので、StoryboardCandidates が使う
      // React Query キャッシュ (['storyboards', scenarioId]) から選択中の素材IDを引く。
      // 非アクティブなシナリオや再読込直後はキャッシュが無いことがあるため、
      // その場合は同じ queryKey で取得してから引く (未取得だと絵コンテが ZIP に入らない)。
      let storyboardMaterialId: string | undefined;
      if (scenario.frame_mode === 'storyboard') {
        let storyboards = queryClient.getQueryData<Storyboard[]>(['storyboards', scenario.id]);
        if (storyboards === undefined) {
          storyboards = await queryClient.fetchQuery({
            queryKey: ['storyboards', scenario.id],
            queryFn: () => listStoryboards(scenario.id),
          });
        }
        storyboardMaterialId = storyboards?.find(sb => sb.is_selected)?.material_id ?? undefined;
      }

      return {
        scenarioId: scenario.id,
        title: scenario.title,
        summary: scenario.summary,
        aspectRatio: scenario.aspect_ratio,
        resolution: scenario.output_resolution,
        // BGM も「採用しない (bgm_excluded)」ならエクスポートに含めない
        bgmMaterialId: scenario.bgm_excluded ? undefined : scenario.bgm_material_id,
        storyboardMaterialId,
        frameMode: scenario.frame_mode,
        scenes: scenesData,
      };
    },
    [scenePreviewMaterials, sceneLastFrames, confirmedFirstFrames, sceneVideoMaterialId, queryClient],
  );

  /**
   * ZIP に入らなかった素材を必ずユーザーに伝える。
   * 取得失敗を握り潰すと「動画が紐づいているのに絵コンテとJSONしか入っていない」
   * という状態が無言で起きるため、成功トーストで上書きしない。
   */
  const notifyFailedItems = useCallback((failedItems: string[], successMessage: string) => {
    if (failedItems.length === 0) {
      toast.success(successMessage);
      return;
    }
    const head = failedItems.slice(0, 3).join('、');
    const rest = failedItems.length > 3 ? ` ほか${failedItems.length - 3}件` : '';
    toast.error(`${failedItems.length}件の素材を取得できませんでした（${head}${rest}）`);
  }, [toast]);

  const handleDownloadScenario = useCallback(async (scenario: Scenario) => {
    if (isDownloading) return;

    setIsDownloading(true);
    toast.show('ダウンロードを準備中...');

    try {
      const downloadData = await collectScenarioDownloadData(scenario);

      const { failedItems } = await downloadScenarioAsZip(downloadData, (progress) => {
        toast.show(`ダウンロード中... ${progress.current}/${progress.total} (${progress.message})`);
      });

      notifyFailedItems(failedItems, 'ダウンロードが完了しました');
    } catch (error) {
      console.error('Failed to download scenario:', error);
      toast.error('ダウンロードに失敗しました');
    } finally {
      setIsDownloading(false);
    }
  }, [isDownloading, toast, collectScenarioDownloadData, notifyFailedItems]);

  const handleCopyScenarioJson = useCallback(async (scenario: Scenario) => {
    if (!scenario) {
      toast.error('コピーするシナリオがありません');
      return;
    }

    try {
      // ファイルダウンロード (ZIP 内 _scenario.json) と全く同じ内容をコピーする。
      // 素材IDの解決 (確定フレーム・最後のフレーム除外判定・動画素材) を DL と共通化
      // するため collectScenarioDownloadData を通し、生成プロンプト等の内部フィールドを
      // 含まない基本要素の JSON を buildScenarioExportJson で組み立てる。
      const downloadData = await collectScenarioDownloadData(scenario);
      const jsonString = JSON.stringify(buildScenarioExportJson(downloadData), null, 2);
      await navigator.clipboard.writeText(jsonString);
      toast.success('シナリオJSONをクリップボードにコピーしました');
    } catch (error) {
      console.error('クリップボードへのコピーに失敗:', error);
      toast.error('クリップボードへのコピーに失敗しました');
    }
  }, [collectScenarioDownloadData, toast]);

  return {
    handleDownloadMaterial,
    handleDownloadScenario,
    handleCopyScenarioJson,
    downloadingMaterials,
    isDownloading,
  };
}
